// Client proof for web sign-in: an invisible Cloudflare Turnstile check bound to
// a one-time server nonce (spec: docs/superpowers/specs/2026-10-08-client-proof-attestation-design.md).
// Every function here degrades to "no header" rather than blocking a sign-in;
// the server decides what a missing or failed proof means.
import axios from 'axios';

// Read per call (CRA still inlines it at build time) so tests can vary it.
const siteKey = () => process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const LAST_PROOF_KEY = 'client_proof_at';
const RENEW_MS = 24 * 60 * 60 * 1000;
// A proof must degrade, never block a sign-in or a queued refresh.
const PROOF_TIMEOUT_MS = 15000;
// Once Cloudflare shows a visible challenge a person has to act, so the deadline
// stretches to 2 minutes — still inside the 5-minute server nonce.
const INTERACTIVE_TIMEOUT_MS = 120000;
// A prepared proof is used only while both halves are still valid: the server
// nonce lives 5 minutes (client_proof.NONCE_TTL) and a Turnstile token 300 s.
const PREPARED_MAX_AGE_MS = 4 * 60 * 1000;

// purpose -> { at, promise }. Single-use: taking one removes it.
const prepared = new Map();

let scriptPromise = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => { scriptPromise = null; reject(new Error('turnstile_load_failed')); };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

// One widget per token: Turnstile tokens are single-use, and registerFull()
// needs one for /register and another for the login that follows it.
// `handle.cancel()` tears the widget down and rejects (used by the overall timeout);
// `handle.onInteractive()` lets the caller extend that timeout for a visible challenge.
function turnstileToken(turnstile, action, cData, handle) {
  return new Promise((resolve, reject) => {
    if (handle.cancelled) { reject(new Error('turnstile_timeout')); return; }
    const host = document.createElement('div');
    Object.assign(host.style, { position: 'fixed', bottom: '16px', right: '16px', zIndex: '10000' });
    document.body.appendChild(host);
    let widgetId = null;
    const finish = (fn, value) => {
      try { if (widgetId !== null) turnstile.remove(widgetId); } catch (e) { /* already gone */ }
      host.remove();
      fn(value);
    };
    handle.cancel = () => finish(reject, new Error('turnstile_timeout'));
    try {
      widgetId = turnstile.render(host, {
        sitekey: siteKey(),
        action,
        cData,
        appearance: 'interaction-only', // invisible unless Cloudflare needs a click
        callback: (token) => finish(resolve, token),
        'before-interactive-callback': () => { if (handle.onInteractive) handle.onInteractive(); },
        'error-callback': (code) => finish(reject, new Error(`turnstile_error_${code}`)),
        'timeout-callback': () => finish(reject, new Error('turnstile_timeout')),
        'unsupported-callback': () => finish(reject, new Error('turnstile_unsupported')),
      });
    } catch (e) {
      finish(reject, new Error('turnstile_render_failed'));
    }
  });
}

const encode = (obj) => btoa(JSON.stringify(obj)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export function proofDue() {
  try {
    return Date.now() - Number(localStorage.getItem(LAST_PROOF_KEY) || 0) > RENEW_MS;
  } catch (e) {
    return true;
  }
}

export function markProofStale() {
  try { localStorage.removeItem(LAST_PROOF_KEY); } catch (e) { /* storage blocked */ }
}

function markProved() {
  try { localStorage.setItem(LAST_PROOF_KEY, String(Date.now())); } catch (e) { /* storage blocked */ }
}

// Start a proof before it is needed — the sign-in page calls this on mount —
// so the 1–4 s Turnstile check runs while the user types, not after they click.
// Idempotent: a still-fresh prepared proof for this purpose is left alone.
export function prepareClientProof(apiUrl, purpose) {
  if (!siteKey()) return;
  const have = prepared.get(purpose);
  if (have && Date.now() - have.at < PREPARED_MAX_AGE_MS) return;
  prepared.set(purpose, { at: Date.now(), promise: buildProof(apiUrl, purpose) });
}

export async function clientProofHeaders(apiUrl, purpose) {
  const have = prepared.get(purpose);
  prepared.delete(purpose);
  if (have && Date.now() - have.at < PREPARED_MAX_AGE_MS) return have.promise;
  return buildProof(apiUrl, purpose);
}

async function buildProof(apiUrl, purpose) {
  if (!siteKey()) return {};
  let nonce;
  try {
    ({ data: { nonce } } = await axios.post(`${apiUrl}/auth/challenge`, null, { timeout: PROOF_TIMEOUT_MS }));
  } catch (e) {
    return {};
  }
  const handle = { cancelled: false, cancel: null, onInteractive: null };
  let timer;
  const deadline = new Promise((_, reject) => {
    const expire = () => {
      handle.cancelled = true;
      if (handle.cancel) handle.cancel();
      reject(new Error('turnstile_timeout'));
    };
    timer = setTimeout(expire, PROOF_TIMEOUT_MS);
    handle.onInteractive = () => {
      if (handle.cancelled) return;
      clearTimeout(timer);
      timer = setTimeout(expire, INTERACTIVE_TIMEOUT_MS);
    };
  });
  try {
    const token = await Promise.race([
      loadTurnstile().then((turnstile) => turnstileToken(turnstile, purpose, nonce, handle)),
      deadline,
    ]);
    clearTimeout(timer);
    markProved();
    return { 'X-Client-Proof': encode({ nonce, turnstile: token }) };
  } catch (e) {
    clearTimeout(timer);
    return { 'X-Client-Proof': encode({ nonce, error: String(e.message).slice(0, 60) }) };
  }
}

export function clientProofMessage(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 403 && detail === 'client_not_verified') {
    return "We couldn't verify this browser. Please refresh the page and try again.";
  }
  if (status === 428) {
    return 'Please refresh the page and try again.';
  }
  return null;
}
